import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Variable } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import {
  applicationsApi,
  type ApplicationDetail,
} from "../api/applicationsApi";

/**
 * Call / flow variables for a single application. Route: /applications/:id/variables
 */
export function ApplicationVariablesPage() {
  const { applicationId: rawId } = useParams<{ applicationId: string }>();
  const applicationId = Number(rawId);
  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(applicationId) || applicationId <= 0) {
      setError("Invalid application.");
      return;
    }
    let active = true;
    setError(null);
    applicationsApi
      .getDetail(applicationId)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(
          err instanceof Error ? err.message : "Failed to load application",
        );
      });
    return () => {
      active = false;
    };
  }, [applicationId]);

  const appName = detail?.applicationName ?? "Application";
  const backTo = `/applications/${applicationId}`;

  return (
    <>
      <PageHeader
        title="Variables"
        actions={
          <Link
            to={backTo}
            aria-label="Back to application"
            className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
          >
            <ArrowLeft size={14} /> {appName}
          </Link>
        }
      />
      <div className="dashboard">
        {error && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            {error}
          </div>
        )}
        {!error && !detail && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading…
          </div>
        )}
        {detail && (
          <section className="tile" aria-labelledby="variables-intro">
            <header
              className="tile-head"
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              <span
                aria-hidden
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: "var(--portal-primary-soft)",
                  color: "var(--portal-primary-darker)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Variable size={20} />
              </span>
              <h2 id="variables-intro" style={{ margin: 0, fontSize: "1.1rem" }}>
                Variables for {detail.applicationName}
              </h2>
            </header>
            <p style={{ margin: 0, color: "var(--portal-muted)", lineHeight: 1.5 }}>
              Manage call variables and flow data for this application. Content
              will be connected here as the feature is expanded.
            </p>
          </section>
        )}
      </div>
    </>
  );
}
